import { Settings, onSettingsChange } from "../ui/settings";
import { BUS_LEVELS, LAST_STAND_MIX, admitVoice, busGains, answerStillFresh, cueSource, cueStart, heartbeatGain, isPlayerAnswer, lastStandCutoff, liveVoices, pickVariant } from "./mixPolicy";
import { BuildCueLead } from "./buildCues";
import type { PackState } from "./mixPolicy";
import { playHeartbeat, playSynth } from "./synth";
import { CUE_MIX, DUCK, GameAudioMix } from "./audioCues";
import { buildGraph } from "./audioGraph";
import type { AudioCue, IAudio, MusicTrack } from "./audioPort";
import { MusicBeds } from "./musicBeds";
import { preferAmbientSession } from "./audioSession";
import { AudioLifecycle, browserLifecycleEnv } from "./audioLifecycle";
import { HumLoop } from "./humLoop";

export type { AudioCue, IAudio, MusicTrack } from "./audioPort";

/**
 * The game's audio. Cues play from the v6 packs in `art/audio` (recorded material, see `art/audio/v6/README.md`); the
 * synthesised envelopes in `synth.ts` stand in only if the packs fail to load (mixPolicy `cueSource`). Music is one
 * looping bed at a time, cross-faded, looped at the composed bar length from `music_manifest.json` rather than the
 * decoded buffer's end (so MP3 padding never lands in the seam). Only the bed that is playing stays decoded (about
 * 30 MB of PCM each); the other is kept as its compressed bytes and decoded again on the next switch (musicBeds.ts). One
 * AudioContext at a time, created by `load` at boot and resumed by the first gesture; everything goes through a limiter.
 * Backgrounding, interruptions and a context WebKit kills are handled by `audioLifecycle.ts`, which may replace the
 * context: `recreate` rebuilds the graph on the new one and the bed carries on where it was.
 * Rate-limited per cue and capped in voices so a horde dying at once does not stack fifty clicks.
 */
export class GameAudio implements IAudio {
  /** Music sits under the effects; these are multipliers on the master level (Settings → Music / Effects scale them). */
  static readonly MusicLevel = BUS_LEVELS.Music;
  static readonly SfxLevel = BUS_LEVELS.Sfx;
  static readonly FadeSeconds = MusicBeds.FadeSeconds;
  /** Master level changes glide with this time constant (s) instead of stepping, which clicks. */
  static readonly GainGlide = 0.02;
  /** A ducking stinger holds the music at this fraction of its level, entering and leaving with these time constants (s). */
  static readonly DuckLevel = GameAudioMix.DuckLevel;
  static readonly DuckAttack = 0.03;
  static readonly DuckRelease = 0.25;
  /** While a level is paused the bed holds at this fraction of its level, gliding there with this time constant (s). */
  static readonly PausedLevel = 0.3;
  static readonly PauseGlide = 0.15;
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private musicGain: GainNode | null = null;
  /** The background beds (musicBeds.ts), on the music bus. */
  private readonly beds = new MusicBeds(() => (this.ctx && this.musicGain ? { ctx: this.ctx, bus: this.musicGain } : null));
  /** Decoded variants per cue (`sfx_<cue>_<n>.mp3`, counts from `sfx_manifest.json`); a random one plays each time, never the last one again. */
  private readonly variants = new Map<AudioCue, AudioBuffer[]>();
  private pack: PackState = "pending";
  /** Every variant decode has finished, so a cue with none decoded falls back to the synth. */
  private packSettled = false;
  /** Context time each sample voice ends, for the voice cap (mixPolicy liveVoices: counted by the clock, not onended). */
  private readonly voiceEnds: number[] = [];
  /** A player answer asked for while the context was not running (mixPolicy answerStillFresh); plays when it runs. */
  private heldAnswer: { cue: AudioCue; delay: number; askedAt: number } | null = null;
  /** The duck in force (level, and the context time it releases), so a lighter one never cuts a deeper one short. */
  private ducked: { level: number; until: number } | null = null;
  private muted = !Settings.current.audio;
  private readonly lastAt = new Map<AudioCue, number>();
  /** The variant each cue played last, so the next is another one (mixPolicy pickVariant). */
  private readonly lastVariant = new Map<AudioCue, number>();
  private volume = 0.7;
  /** Settings → Music and Effects (0..1), under the master volume. */
  private busMusic = Settings.current.music;
  private busSfx = Settings.current.sfx;
  private readonly cueListeners: ((cue: AudioCue) => void)[] = [];
  private pausedDuck = false;
  /** The music bus's last-stand low-pass and level (musicGain → filter → gain → master). */
  private standFilter: BiquadFilterNode | null = null;
  private standGain: GainNode | null = null;
  private stand: number | null = null;
  /** Suspends in the background, resumes (or replaces the context) on the way back: made with the first context. */
  private life: AudioLifecycle<AudioContext> | null = null;

  constructor() {
    // main.ts pushes the master volume and the mute; the two buses follow Settings from here.
    if (typeof document !== "undefined") onSettingsChange(() => this.setBusLevels(Settings.current.music, Settings.current.sfx));
  }

  /** Browsers need a user gesture before audio starts: call from every touch/click (cheap once running). */
  unlock(): void {
    // WebKit also reports "interrupted" (a call, another app's audio): anything but running wants a resume, or a new context.
    if (this.context()) this.life?.gesture();
  }

  /** The context, made on first use; it starts suspended until `unlock` runs inside a gesture. */
  private context(): AudioContext | null {
    if (this.ctx) return this.ctx;
    if (typeof window === "undefined") return null;
    const ctx = this.build();
    if (!ctx) return null;
    if (!this.life) {
      this.life = new AudioLifecycle<AudioContext>({
        context: () => this.context(),
        recreate: (dead) => this.recreate(dead),
        running: () => this.resumed(),
      }, browserLifecycleEnv());
      this.life.start();
    }
    this.life.watch(ctx);
    return ctx;
  }

  /** A new context with the whole graph at the current settings (audioGraph.ts). */
  private build(): AudioContext | null {
    const w = window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext };
    const Ctor = w.AudioContext ?? w.webkitAudioContext;
    if (!Ctor) return null;
    preferAmbientSession();
    const ctx = new Ctor();
    const g = buildGraph(ctx, { master: this.level(), sfx: busGains(this.busMusic, this.busSfx).sfx, music: this.musicLevel(), stand: this.stand });
    this.master = g.master; this.sfxGain = g.sfxGain; this.musicGain = g.musicGain; this.standFilter = g.standFilter; this.standGain = g.standGain;
    this.ctx = ctx;
    return ctx;
  }

  /**
   * WebKit killed the context (closed, stuck "interrupted", a frozen clock): close it, build a new one and start the
   * bed on it where it was. Everything kept in the old context's time (rate limits, voices, the duck) starts over,
   * since the new clock starts at 0; the decoded effects carry over (an AudioBuffer belongs to no context).
   */
  private recreate(dead: AudioContext): AudioContext | null {
    const deadNow = dead.currentTime;
    if (dead.state !== "closed") dead.close().catch(() => undefined);   // iOS allows only a few live contexts
    this.ctx = null;
    this.lastAt.clear();
    this.voiceEnds.length = 0;
    this.ducked = null;
    const ctx = this.context();
    if (ctx) this.beds.reattach(deadNow);
    return ctx;
  }

  /**
   * The context runs again. The bed plays on by itself after a resume (its source kept its place); asking for it again
   * starts it only if it is missing. The tap that places a tower resumes a suspended context from its pointerdown, but
   * WebKit reports "running" only once the resume settles, after the placement's cue was asked for: that cue plays now
   * if it is still fresh.
   */
  private resumed(): void {
    this.beds.music(this.beds.track);
    const held = this.heldAnswer;
    if (!held) return;
    this.heldAnswer = null;
    // the time the resume took comes off its delay (past zero it is late: a build cue skips its lead-in to catch up)
    const now = performance.now() / 1000;
    if (answerStillFresh(held.askedAt, now)) this.sound(held.cue, held.delay - (now - held.askedAt));
  }

  /**
   * Fetches the packs in the background: the loop manifest and the bed wanted first (the hub, at boot) ahead of
   * everything else, so it starts the moment it decodes; the effect variants decode after it.
   */
  async load(resolve: (url: string) => string): Promise<void> {
    const ctx = this.context();
    if (!ctx) return;
    const fetchBytes = async (name: string): Promise<ArrayBuffer | null> => {
      try {
        const res = await fetch(resolve(`./audio/${name}.mp3`));
        return res.ok ? await res.arrayBuffer() : null;
      } catch { return null; }
    };
    const beds = this.beds.fetch(resolve, fetchBytes);
    let manifest: Record<string, number> | null = null;
    try { manifest = (await (await fetch(resolve("./audio/sfx_manifest.json"))).json()) as Record<string, number>; } catch { /* no pack: synth fallback */ }
    this.pack = manifest ? "ready" : "failed";
    const firstBed = beds[0];
    const jobs: Promise<void>[] = [];
    for (const [cue, count] of Object.entries(manifest ?? {})) {
      const list: AudioBuffer[] = [];
      this.variants.set(cue as AudioCue, list);
      for (let i = 1; i <= count; i++) {
        jobs.push(fetchBytes(`sfx_${cue}_${i}`).then(async (bytes) => {
          await firstBed;
          if (!bytes) return;
          try { list.push(await (this.ctx ?? ctx).decodeAudioData(bytes)); } catch { /* this variant stays out */ }
        }));
      }
    }
    await Promise.all(beds);
    this.beds.settle();
    await Promise.all(jobs);
    this.packSettled = true;
  }

  get track(): MusicTrack | null { return this.beds.track; }
  /** Debug read (`__td.audio.state`, the headless background check): the context and the bed on the speakers. */
  get state(): { ctx: string; time: number; track: MusicTrack | null; playing: MusicTrack | null; armed: boolean } {
    return { ctx: this.ctx?.state ?? "none", time: this.ctx?.currentTime ?? 0, track: this.beds.track, playing: this.beds.playingTrack, armed: this.life?.isArmed ?? false };
  }
  music(track: MusicTrack | null): void { this.beds.music(track); }

  setMuted(muted: boolean): void { this.muted = muted; this.applyGain(); }
  /** Master level 0..1 (the settings slider); 0.7 matches the old fixed gain. */
  setVolume(volume: number): void { this.volume = Math.max(0, Math.min(1, volume)); this.applyGain(); }
  private level(): number { return this.muted ? 0 : this.volume * 0.5; }
  private applyGain(): void { if (this.master && this.ctx) this.master.gain.setTargetAtTime(this.level(), this.ctx.currentTime, GameAudio.GainGlide); }

  setBusLevels(music: number, sfx: number): void {
    this.busMusic = music; this.busSfx = sfx;
    if (!this.ctx || !this.sfxGain || !this.musicGain) return;
    const now = this.ctx.currentTime;
    this.sfxGain.gain.setTargetAtTime(busGains(music, sfx).sfx, now, GameAudio.GainGlide);
    const g = this.musicGain.gain;
    g.cancelScheduledValues(now);                                  // a duck in flight would release to the old level
    this.ducked = null;
    g.setValueAtTime(g.value, now);
    g.setTargetAtTime(this.musicLevel(), now, GameAudio.GainGlide);
  }

  onCue(l: (cue: AudioCue) => void): () => void {
    this.cueListeners.push(l);
    return () => { const i = this.cueListeners.indexOf(l); if (i >= 0) this.cueListeners.splice(i, 1); };
  }

  /**
   * The title's diorama (render/titleDiorama.ts) is a real battle under the title music: its shots, hits and deaths
   * would play over the title bed (and buzz the haptics that listen to the cues). While held, only the UI click plays.
   */
  private sfxHeld = false;
  holdSfx(on: boolean): void { this.sfxHeld = on; if (on) this.hum(0, 0); }

  /** Plays a cue `delay` s from now; `pitch` scales its playback rate (the chest stage's rattles and tier-ups climb). */
  play(cue: AudioCue, delay = 0, pitch = 1): void {
    if (this.sfxHeld && cue !== "ui") return;
    for (const l of this.cueListeners) l(cue);
    this.sound(cue, delay, pitch);
  }

  /** The cue itself, once its listeners have heard it (a held player answer comes back here, not through `play`). */
  private sound(cue: AudioCue, delay: number, pitch = 1): void {
    const ctx = this.ctx;
    if (!ctx || !this.master || !this.sfxGain || this.muted) return;
    // Not running (before the first gesture, backgrounded, interrupted): drop the cue, or they queue and burst out on
    // resume. A player answer is held for the resume its own tap set off (see the statechange listener).
    if (ctx.state !== "running") {
      if (isPlayerAnswer(cue) && typeof document !== "undefined" && !document.hidden) {
        this.heldAnswer = { cue, delay, askedAt: performance.now() / 1000 };
        ctx.resume().catch(() => undefined);
      }
      return;
    }
    const start = cueStart(delay, BuildCueLead[cue] ?? 0);
    const at = ctx.currentTime + start.wait;
    const m = CUE_MIX[cue];
    if ((this.lastAt.get(cue) ?? -Infinity) > at - m.gap) return;
    const list = this.variants.get(cue);
    const source = cueSource(this.pack, list?.length, this.packSettled);
    if (source === "none") return;
    if (source === "synth") { this.lastAt.set(cue, at); playSynth(ctx, this.sfxGain, cue, at); return; }
    if (!list || !admitVoice(cue, liveVoices(this.voiceEnds, ctx.currentTime))) return;
    this.lastAt.set(cue, at);
    const pick = pickVariant(list.length, this.lastVariant.get(cue) ?? -1, Math.random());
    this.lastVariant.set(cue, pick);
    const buffer = list[pick];
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const rate = (1 + (Math.random() * 2 - 1) * m.spread) * pitch;
    src.playbackRate.value = rate;
    const g = ctx.createGain();
    g.gain.value = m.gain;
    src.connect(g).connect(this.sfxGain);
    const length = Math.max(0, buffer.duration - start.offset);
    this.voiceEnds.push(at + length / rate);
    src.start(at, start.offset);
    const duck = DUCK[cue];
    if (duck !== undefined) this.duck(at, length, duck);
  }

  /** Pulls the music bus down to `level` of itself under a cue from `at` for `hold` seconds, then lets it back up. */
  private duck(at: number, hold: number, level: number): void {
    if (!this.musicGain) return;
    // A placement landing inside a stinger's deeper duck leaves it be: its own release would lift the bed under the stinger.
    const d = this.ducked;
    if (d && d.until > at && d.level < level) return;
    this.ducked = { level, until: at + hold };
    const g = this.musicGain.gain;
    g.cancelScheduledValues(at);
    g.setTargetAtTime(this.musicLevel() * level, at, GameAudio.DuckAttack);
    g.setTargetAtTime(this.musicLevel(), at + hold, GameAudio.DuckRelease);
  }

  pauseDuck(on: boolean): void {
    if (on === this.pausedDuck) return;
    this.pausedDuck = on;
    if (!this.musicGain || !this.ctx) return;
    const g = this.musicGain.gain, now = this.ctx.currentTime;
    g.cancelScheduledValues(now);                                  // a stinger's pending release must not undo it
    this.ducked = null;
    g.setValueAtTime(g.value, now);
    g.setTargetAtTime(this.musicLevel(), now, GameAudio.PauseGlide);
  }
  lastStand(depth: number | null): void {
    if (depth === this.stand) return;
    this.stand = depth;
    if (!this.ctx || !this.standFilter || !this.standGain) return;
    const now = this.ctx.currentTime;
    // setTargetAtTime from wherever the glide is now: a depth that changes mid-glide never steps (it would click)
    this.standFilter.frequency.setTargetAtTime(lastStandCutoff(depth), now, LAST_STAND_MIX.GlideSeconds);
    this.standGain.gain.setTargetAtTime(depth === null ? 1 : LAST_STAND_MIX.DuckLevel, now, LAST_STAND_MIX.GlideSeconds);
  }

  /** The Arcane Spires' held hum (humLoop.ts); silent when muted, while the level is paused, or with no context running. */
  private humLoop: HumLoop | null = null;
  hum(level: number, ramp: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.sfxGain) return;
    if (this.humLoop && this.humLoop.ctx !== ctx) { this.humLoop.stop(); this.humLoop = null; }
    const on = !this.muted && !this.pausedDuck && !this.sfxHeld && ctx.state === "running" ? level : 0;
    if (on <= 0 && !this.humLoop) return;
    (this.humLoop ??= new HumLoop(ctx, this.sfxGain)).set(on, ramp);
  }

  heartbeat(depth: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.sfxGain || this.muted || ctx.state !== "running") return;
    playHeartbeat(ctx, this.sfxGain, ctx.currentTime, heartbeatGain(depth));
  }

  /** The music bus's resting level: lower while a level is paused. */
  private musicLevel(): number { return busGains(this.busMusic, this.busSfx).music * (this.pausedDuck ? GameAudio.PausedLevel : 1); }
}

/** The one audio instance for the app; presentation code imports this rather than constructing its own. */
export const audio = new GameAudio();
